import { createContext, useContext, useEffect, useRef, useState } from "react";
import { User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useUserData } from "@/hooks/auth/useUserData";
import { supabase } from "@/integrations/supabase/client";
import { identifyContact, trackEvent } from "@/lib/hubspot";
import { ImpersonationService } from "@/services/users/ImpersonationService";
import { UserService } from "@/services/users/UserService";
import { buildSignupMetadata } from "@/lib/landingAttribution";
import BrandSpinner from "@/components/ui/BrandSpinner";

import type { Profile, Team } from "@/types/auth/auth";

interface AuthContextType {
  user: User | null;
  userProfile: Profile | null | undefined; // undefined: not checked, null: checked and not found
  team: Team | null;
  loading: boolean;
  accessToken: string | null;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signUp: (
    email: string,
    password: string,
    accountType: "consumer" | "business",
    teamId?: string,
    fullName?: string,
    invitationId?: string,
    metadata?: Record<string, unknown>,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  refreshTeam: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: undefined,
  team: null,
  loading: true,
  accessToken: null,
  signIn: async () => ({ data: null, error: null }),
  signUp: async () => {},
  signOut: async () => {},
  refreshTeam: async () => {},
  refreshProfile: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | undefined>(undefined);

  const userDataQuery = useUserData(user?.id);

  const refreshTeam = async () => {
    if (user) {
      await userDataQuery.refetch();
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await userDataQuery.refetch();
    }
  };

  const teamIdRef = useRef(userDataQuery.data?.team?.id);
  teamIdRef.current = userDataQuery.data?.team?.id;

  useEffect(() => {
    const channel = supabase
      .channel("team-updates")
      .on(
        "broadcast",
        { event: "member_joined" },
        ({ payload }: { payload: { teamId: number; userId: string } }) => {
          if (payload.teamId === teamIdRef.current) {
            userDataQuery.refetch();
          }
        },
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Don't override if we're impersonating
      if (!ImpersonationService.isImpersonating()) {
        if (lastUserIdRef.current !== session?.user?.id) {
          lastUserIdRef.current = session?.user?.id;
          setUser(session?.user ?? null);
        }
        if (session?.access_token) {
          setAccessToken(session.access_token);
        }
        if (_event === "INITIAL_SESSION") {
          setAuthLoading(false);
        }
      }
      if (_event === "SIGNED_IN") {
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        const hasCookie = document.cookie.split(";").some((c) => c.trim().startsWith("hg_session="));
        if (!isLocalhost && !hasCookie) {
          UserService.generateSsoToken()
            .then((token) => {
              document.cookie = `hg_session=${encodeURIComponent(token)}; domain=.helpgenie.ai; path=/; max-age=300; SameSite=None; Secure`;
            })
            .catch(() => {});
        }
      }

      if (_event === "SIGNED_OUT") {
        document.cookie = "hg_session=; domain=.helpgenie.ai; path=/; max-age=0; SameSite=None; Secure";
        setAccessToken(null);
        queryClient.removeQueries({ queryKey: ["auth"] });
      }
    });

    const fetchSession = async () => {
      try {
        // Check if impersonating - if so, create a fake user
        const impersonationState = ImpersonationService.getImpersonationState();
        if (impersonationState) {
          console.log(`🎭 Impersonating user: ${impersonationState.impersonatedUserId}`);
          const fakeUser = {
            id: impersonationState.impersonatedUserId,
            email: `impersonated_${impersonationState.impersonatedUserId}@local`,
            app_metadata: { provider: "impersonation" },
            user_metadata: { impersonating: true, real_user_id: impersonationState.adminUserId },
            aud: "authenticated",
            created_at: new Date().toISOString(),
            confirmation_sent_at: null,
            email_confirmed_at: null,
            phone_confirmed_at: null,
            is_super_admin: false,
            role: "authenticated",
            last_sign_in_at: new Date().toISOString(),
            identities: null,
          } as any;
          setUser(fakeUser);
          setAuthLoading(false);
          return;
        }

        // One-time cleanup of legacy token key from older versions
        try {
          localStorage.removeItem("supabase.auth.token");
        } catch {
          /* ignore */
        }
        // onAuthStateChange(INITIAL_SESSION) handles setting user/accessToken and authLoading
      } catch (e) {
        console.error("An unexpected error occurred:", e);
        setAuthLoading(false);
      }
    };

    fetchSession();

    // Safety net: onAuthStateChange should fire INITIAL_SESSION almost immediately,
    // but if the Supabase client stalls (slow network, cold start), don't leave the
    // app stuck on the loading spinner forever — fall back to unauthenticated.
    const initialSessionTimeout = window.setTimeout(() => {
      setAuthLoading(false);
    }, 8_000);

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(initialSessionTimeout);
    };
  }, [queryClient]);

  useEffect(() => {
    const handleImpersonationChange = (e: StorageEvent) => {
      if (e.key !== "userAgents" && e.key !== "admin_impersonation_state") return;
      queryClient.invalidateQueries({ queryKey: ["auth", "userData"] });
    };

    window.addEventListener("storage", handleImpersonationChange);
    return () => window.removeEventListener("storage", handleImpersonationChange);
  }, [queryClient]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Check and set timezone via API (fire-and-forget)
    if (data.user && !error) {
      identifyContact(email);
      supabase.functions
        .invoke("api", {
          body: JSON.stringify({
            resource: "profiles",
            action: "check-timezone",
          }),
        })
        .catch((err) => {
          console.error("Error checking/updating timezone on signin:", err);
        });
    }

    return { data, error };
  };

  const signUp = async (
    email: string,
    password: string,
    accountType: "consumer" | "business",
    teamId?: string,
    fullName?: string,
    invitationId?: string,
    metadata?: Record<string, unknown>,
  ) => {
    console.log("Starting signup process:", { email, accountType, teamId });

    // Every sign-up path (SignUp, business/consumer onboarding) carries the
    // same attribution: first and last touch, click ids, the landing-pages
    // visitor id and the GA4 client id. No migration: it lands in
    // auth.users.raw_user_meta_data. See @/lib/landingAttribution.
    const signupData = { ...buildSignupMetadata(), ...(metadata ?? {}) };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      ...(Object.keys(signupData).length > 0 ? { options: { data: signupData } } : {}),
    });

    if (error) {
      console.error("Supabase signup error:", error);
      throw error;
    }

    console.log("Supabase signup successful:", data);

    if (data.user) {
      try {
        // Detect user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const { data: profileResult, error: profileError } = await supabase.functions.invoke("handle-profile", {
          body: {
            mode: "create",
            userId: data.user.id,
            accountType,
            teamId,
            invitationId,
            timezone,
            full_name: fullName?.trim() || undefined,
          },
        });

        if (profileError) {
          console.error("❌ Error calling handle-profile function:", profileError);
          throw profileError;
        }

        console.log("✅ Profile created successfully:", profileResult);

        // Team attachment (from invite acceptance) happens server-side inside handle-profile;
        // the cached userData query (staleTime: Infinity) won't pick it up on its own.
        await refreshTeam();

        const [firstname, ...lastnameParts] = fullName?.trim().split(/\s+/) ?? [];
        identifyContact(email, {
          hg_account_type: accountType,
          ...(firstname && { firstname }),
          ...(lastnameParts.length > 0 && { lastname: lastnameParts.join(" ") }),
        });
        trackEvent("pe441784197_signup_completed", {
          account_type: accountType,
          has_team_invite: teamId && invitationId ? "true" : "false",
        });
      } catch (profileError) {
        console.error("❌ Error in profile creation:", profileError);
        throw profileError;
      }
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    user,
    userProfile: userDataQuery.data?.profile ?? (userDataQuery.isPending ? undefined : null),
    team: userDataQuery.data?.team ?? null,
    loading: authLoading || (user ? userDataQuery.isPending : false),
    accessToken,
    signIn,
    signUp,
    signOut,
    refreshTeam,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {value.loading ? <BrandSpinner /> : children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
